import { takeLatest, put, select, take } from 'redux-saga/effects';
import actions from './actions';
import types from './types';
import ktRequest from '../../../utils/request';
import moment from 'moment';

const getRoomsNames = state => state.rooms.roomNames;
const getCurrentCenterId = state => state.identity.centerId;
const getSchedulesByWeek = state => state.scheduling.schedules.byWeek;
const getSchedulesById = state => state.scheduling.schedules.byId;

function * getScheduleForWeek (payload) {
  try {
    // eslint-disable-next-line
    const { start, end } = payload;
    const centerId = yield select(getCurrentCenterId);

    const response = yield ktRequest.get(`/scheduling/schedule?centerId=${centerId}&start=${start}`, 'timecards');

    yield put(actions.getScheduleForWeekSuccess(start, response.data));
  } catch (error) {
    yield put(actions.getScheduleForWeekFailure(error));
  }
}

function * createNewSchedule (payload) {
  try {
    const { start, end } = payload;
    const centerId = yield select(getCurrentCenterId);
    const body = {
      start,
      end,
      centerId
    };

    const response = yield ktRequest.post('/scheduling/new', body, 'timecards');

    if (!response.ok) {
      const json = yield response.json();
      throw new Error(json.message || 'Something went wrong.');
    }

    const json = yield response.json();

    yield put(actions.createNewScheduleSuccess(start, json.data));
  } catch (error) {
    yield put(actions.createNewScheduleFailure(error));
  }
}

function * saveSchedule (payload) {
  try {
    const { shifts: shiftsToUpdate, shiftsToRemove, scheduleId } = payload;
    const body = {
      shifts: shiftsToUpdate.filter(shift => shift.removeShiftId === undefined),
      shiftsToRemove,
      scheduleId
    };

    const roomNames = yield select(getRoomsNames);
    const response = yield ktRequest.post('/scheduling/save', body, 'timecards');

    const json = yield response.json();
    const { shifts } = json;

    shifts.map(shift => ({
      ...shift,
      roomName: roomNames[shift.roomId] || ''
    }));

    yield put(actions.saveScheduleSuccess(shifts, payload.scheduleId));
  } catch (error) {
    yield put(actions.saveScheduleFailure(error));
  }
}

function * publishSchedule (payload) {
  try {
    const { scheduleId } = payload;

    const response = yield ktRequest.post('/scheduling/publish', { scheduleId }, 'timecards');

    if (!response.ok) {
      throw new Error('Something went wrong.');
    }

    yield put(actions.publishScheduleSuccess(scheduleId));
  } catch (error) {
    yield put(actions.publishScheduleFailure(error));
  }
}

function * unpublishSchedule (payload) {
  try {
    const { scheduleId } = payload;

    const response = yield ktRequest.post('/scheduling/unpublish', { scheduleId }, 'timecards');

    if (!response.ok) {
      throw new Error('Something went wrong.');
    }

    yield put(actions.unpublishScheduleSuccess(scheduleId));
  } catch (error) {
    yield put(actions.unpublishScheduleFailure(error));
  }
}

function * copyScheduleToNewWeek (payload) {
  try {
    const { scheduleId, start, end } = payload;
    const formattedStart = moment(start).format('YYYY-MM-DD');
    const formattedEnd = moment(end).format('YYYY-MM-DD');

    const body = {
      scheduleId,
      start: formattedStart,
      end: formattedEnd
    };

    const response = yield ktRequest.post('/scheduling/copy', body, 'timecards');

    if (!response.ok) {
      const json = yield response.json();
      throw new Error(json.error || 'Something went wrong. Please try again later.');
    }

    const json = yield response.json();

    yield put(actions.copyExistingScheduleToNewWeekSuccess(formattedStart, json.data));
  } catch (error) {
    yield put(actions.copyExistingScheduleToNewWeekFailure(error));
  }
}

function * copyShiftToNewDates (payload) {
  try {
    const { shift, dates } = payload;
    // check redux if a schedule already exists for a week to prevent an unnecessary network call
    const schedulesByWeek = yield select(getSchedulesByWeek);
    const schedulesById = yield select(getSchedulesById);
    const centerId = yield select(getCurrentCenterId);

    /**
     * iterate over dates (sequentially, not parallel):
     * - if schedule already exists for a date (already fetched and stored in redux)
     * -- continue
     * - else,
     * -- fire network request to create a schedule for the week
     * -- if an error is thrown (400), a schedule already exists for that week but has yet to be fetched on the front end, fetch that schedule
     * -- save schedule to redux
     * - finally
     * -- adjust shift information so that it is the same date as the requested copy date
     * -- create DRAFT id, `[schedule_id]__[Date.now()]__DRAFT`
     * -- insert adjusted shift into schedule in redux
     */
    for (const idx in dates) {
      try {
        const date = dates[idx];
        const clonedShift = cloneShiftForDate(shift, date);
        const clonedShiftStartOfWeekKey = moment(clonedShift.start).startOf('week').format('YYYY-MM-DD');
        const clonedShiftEndOfWeekKey = moment(clonedShift.start).endOf('week').format('YYYY-MM-DD');

        if (schedulesByWeek[clonedShiftStartOfWeekKey]) {
          const schedule = schedulesById[schedulesByWeek[clonedShiftStartOfWeekKey]];

          // shifts can only be added to schedules that are in a DRAFT state
          if (schedule.publishStatus === 'DRAFT') {
            clonedShift.id = createShiftDraftId(schedule.scheduleId);

            yield put(actions.saveShiftToWeekSchedule(clonedShift, schedule.scheduleId));
          }
        } else {
          const response = yield ktRequest.post('/scheduling/new', {
            start: clonedShiftStartOfWeekKey,
            end: clonedShiftEndOfWeekKey,
            centerId
          }, 'timecards');

          if (!response.ok) {
            // shedule already exists for this week, fetch it
            yield put(actions.getScheduleForWeekRequest(clonedShiftStartOfWeekKey, clonedShiftEndOfWeekKey));
            yield take(types.GET_SCHEDULE_FOR_WEEK_SUCCESS);
          } else {
            // schedule does not exist for this week
            const json = yield response.json();
            yield put(actions.createNewScheduleSuccess(clonedShiftStartOfWeekKey, json.data));
          }

          // the store has updated so we need to re-select the schedules
          const updatedSchedulesByWeek = yield select(getSchedulesByWeek);
          const fetchedScheduleId = updatedSchedulesByWeek[clonedShiftStartOfWeekKey];
          clonedShift.id = createShiftDraftId(fetchedScheduleId);

          yield put(actions.saveShiftToWeekSchedule(clonedShift, fetchedScheduleId));
        }
      } catch (error) {
        // don't want one error to end the entire loop early
      }
    }

    yield put(actions.copyShiftToNewDatesSuccess());
  } catch (error) {
    console.log(error);
    yield put(actions.copyShiftToNewDatesFailure(error));
  }
}

// HELPERS
// clone the provided shift and update the start and end time to match the provided date
function cloneShiftForDate (shift, date) {
  const { start, end } = shift;
  const selectedStartShiftHours = moment(start).hours();
  const selectedStartShiftMinutes = moment(start).minutes();
  const selectedStartShiftSeconds = moment(start).seconds();
  const selectedStartShiftMilliseconds = moment(start).milliseconds();
  const selectedEndShiftHours = moment(end).hours();
  const selectedEndShiftMinutes = moment(end).minutes();
  const selectedEndShiftSeconds = moment(end).seconds();
  const selectedEndShiftMilliseconds = moment(end).milliseconds();

  const newStart = moment(date)
    .clone()
    .hours(selectedStartShiftHours)
    .minutes(selectedStartShiftMinutes)
    .seconds(selectedStartShiftSeconds)
    .milliseconds(selectedStartShiftMilliseconds);

  const newEnd = moment(date)
    .clone()
    .hours(selectedEndShiftHours)
    .minutes(selectedEndShiftMinutes)
    .seconds(selectedEndShiftSeconds)
    .milliseconds(selectedEndShiftMilliseconds);

  return {
    ...shift,
    start: newStart,
    end: newEnd
  };
}

function createShiftDraftId (scheduleId) {
  return `${scheduleId}__${Date.now()}__DRAFT`;
}

export const schedulingSagas = [
  takeLatest(types.GET_SCHEDULE_FOR_WEEK_REQUEST, getScheduleForWeek),
  takeLatest(types.CREATE_NEW_SCHEDULE_REQUEST, createNewSchedule),
  takeLatest(types.SAVE_SHIFTS_TO_SCHEDULE_REQUEST, saveSchedule),
  takeLatest(types.PUBLISH_SCHEDULE_REQUEST, publishSchedule),
  takeLatest(types.UNPUBLISH_SCHEDULE_REQUEST, unpublishSchedule),
  takeLatest(types.COPY_SCHEDULE_TO_NEW_WEEK_REQUEST, copyScheduleToNewWeek),
  takeLatest(types.COPY_SHIFT_TO_NEW_DATES_REQUEST, copyShiftToNewDates)
];
